import { morph } from '@theme/morph';
import { startViewTransition } from '@theme/utilities';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options ) {
    const { cache = !Shopify.designMode } = options ?? {};

    this.abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache);

    if (!abortController.signal.aborted) {
      this.abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  abortPendingMorph(sectionId) {
    const existingAbortController = this.abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  /**
   * Returns true if the section HTML for the given URL is already cached.
   * @param {string} sectionId - The section ID
   * @param {URL} [url] - The URL to check
   * @returns {boolean}
   */
  isCached(sectionId, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    return this.#cache.has(sectionUrl);
  }

  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch section: ${response.status}`);
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    try {
      const sectionHTML = await pendingPromise;
      if (useCache) {
        this.#cache.set(sectionUrl, sectionHTML);
      }
      return sectionHTML;
    } finally {
      this.#pendingPromises.delete(sectionUrl);
    }
  }

  /**
   * Caches the page sections
   */
  /**
   * Caches all sections on page load for fast re-rendering
   * Skips sections with shadow DOM as they can't be cached properly
   */
  #cachePageSections() {
    if (Shopify.designMode) return;
    const sections = document.querySelectorAll('.shopify-section');
    for (const section of sections) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;

      this.#cache.set(url, section.outerHTML);
    }
  }

  /**
   * Fetches HTML from URL, finds element by selector and morphs it into target element
   * @param {Object} options - The options
   * @param {string} options.sectionId - The section ID to fetch
   * @param {URL} options.url - The URL to fetch from
   * @param {HTMLElement} options.targetElement - The element to morph into
   * @param {string} options.sourceSelector - The selector to find the source element in fetched HTML
   * @param {boolean} [options.cache=false] - Whether to use cache
   * @param {HTMLElement} [options.componentElement] - The component element to manage loading states
   * @param {string} [options.loadedAttribute='data-loaded'] - Attribute name for loaded state
   * @param {string} [options.loadingAttribute='data-loading'] - Attribute name for loading state
   * @param {boolean} [options.checkLoadingStates=false] - Whether to check and manage loading states
   * @param {Function} [options.onSuccess] - Callback on success
   * @param {Function} [options.onError] - Callback on error
   * @returns {Promise<void>}
   */
  async fetchAndMorphContent(options) {
    const {
      sectionId,
      url,
      targetElement,
      sourceSelector,
      cache = false,
      componentElement,
      loadedAttribute = 'data-loaded',
      loadingAttribute = 'data-loading',
      removeHiddenAttribute = false,
      checkLoadingStates = false,
      onBeforeRender,
      onSuccess,
      onError,
      onMorph = true
    } = options;

    if (!targetElement) {
      console.warn('Target element not found');
      return;
    }

    // Prevent duplicate fetches if already loaded or currently loading
    if (checkLoadingStates && componentElement) {
      if (componentElement.hasAttribute(loadedAttribute)) return;
      if (componentElement.hasAttribute(loadingAttribute)) return;
      componentElement.setAttribute(loadingAttribute, '');
    }

    try {
      const resultsMarkup = await this.getSectionHTML(sectionId, cache, url);

      if (!resultsMarkup) {
        if (checkLoadingStates && componentElement) {
          componentElement.removeAttribute(loadingAttribute);
        }
        return;
      }

      const parseHtml = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const sourceElement = parseHtml.querySelector(sourceSelector);

      if (!sourceElement) {
        console.warn(`Source element with selector "${sourceSelector}" not found`);
        if (checkLoadingStates && componentElement) {
          componentElement.removeAttribute(loadingAttribute);
        }
        return;
      }

      // Runs while the old content is still in the DOM, so callers can capture
      // transient state (e.g. an open dropdown) before it is replaced.
      if (onBeforeRender) {
        onBeforeRender(targetElement);
      }

      if (onMorph) {
        morph(targetElement, sourceElement);
      } else {
        targetElement.replaceWith(sourceElement);
      }

      // Update loading states on success
      if (checkLoadingStates && componentElement) {
        componentElement.setAttribute(loadedAttribute, '');
        componentElement.removeAttribute(loadingAttribute);
      }

      if (removeHiddenAttribute) {
        targetElement.removeAttribute('hidden');
      }

      if (onSuccess) {
        onSuccess(sourceElement);
      }
    } catch (error) {
      console.error('Error fetching and morphing content:', error);

      // Cleanup loading state on error
      if (checkLoadingStates && componentElement) {
        componentElement.removeAttribute(loadingAttribute);
      }

      if (onError) {
        onError(error);
      }
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();

/**
 * Hover-prefetch + click-fetch cache shared by components that prefetch content
 * on hover and consume it on click (quick-add, back-in-stock, shoppable-gallery…).
 *
 * Centralises the tricky concurrency rules in one place:
 *  - in-flight dedup (a URL is fetched once even if prefetch + click race),
 *  - `get()` awaits an in-flight prefetch instead of starting a second fetch,
 *  - each new fetch aborts the previous one (avoids stale responses),
 *  - bounded FIFO cache.
 *
 * The fetch + parse/transform stays in the caller via the `fetcher` callback, so
 * each component keeps returning its own shape (Element / Document / html).
 *
 * @template T
 */
export class PrefetchCache {
  /** @type {Map<string, T>} */
  #cache = new Map();
  /** @type {Map<string, Promise<T | null>>} */
  #pending = new Map();
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {(url: string, signal: AbortSignal) => Promise<T | null>} */
  #fetcher;
  /** @type {number} */
  #maxSize;
  /** @type {() => boolean} */
  #shouldCache;

  /**
   * @param {object} options
   * @param {(url: string, signal: AbortSignal) => Promise<T | null>} options.fetcher - Fetches + parses one URL.
   * @param {number} [options.maxSize=20] - Max cached entries (FIFO eviction).
   * @param {() => boolean} [options.shouldCache] - Return false to skip caching (e.g. in the theme editor).
   */
  constructor({ fetcher, maxSize = 20, shouldCache } = {}) {
    this.#fetcher = fetcher;
    this.#maxSize = maxSize;
    this.#shouldCache = shouldCache ?? (() => true);
  }

  /**
   * True if the URL is cached or currently being fetched.
   * @param {string} url
   */
  has(url) {
    return this.#cache.has(url) || this.#pending.has(url);
  }

  /**
   * Fire-and-forget prefetch (e.g. on hover). Deduped against cache + in-flight.
   * @param {string} url
   */
  prefetch(url) {
    if (!url || this.has(url)) return;
    const promise = this.#load(url);
    this.#pending.set(url, promise);
    promise.finally(() => this.#pending.delete(url));
    // If nothing ever awaits a failed prefetch, don't surface an unhandled rejection.
    promise.catch(() => {});
  }

  /**
   * Resolve a URL: cached value → in-flight prefetch → fresh fetch.
   * @param {string} url
   * @returns {Promise<T | null>}
   */
  async get(url) {
    if (!url) return null;
    if (this.#cache.has(url)) return this.#cache.get(url) ?? null;

    const pending = this.#pending.get(url);
    if (pending) {
      try {
        return await pending;
      } catch {
        // A failed in-flight prefetch must not break the click — fall through and refetch.
      }
    }
    return this.#load(url);
  }

  /**
   * @param {string} url
   * @returns {Promise<T | null>}
   */
  async #load(url) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const value = await this.#fetcher(url, this.#controller.signal);
    if (value != null && this.#shouldCache()) this.#set(url, value);
    return value;
  }

  /**
   * @param {string} url
   * @param {T} value
   */
  #set(url, value) {
    if (this.#cache.size >= this.#maxSize) {
      const oldest = this.#cache.keys().next().value;
      if (oldest !== undefined) this.#cache.delete(oldest);
    }
    this.#cache.set(url, value);
  }

  clear() {
    this.#cache.clear();
    this.#pending.clear();
  }

  abort() {
    this.#controller?.abort();
    this.#controller = null;
  }
}
